import { useRef, useState, useEffect, useCallback } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(ScrollTrigger, useGSAP);

/* ════════════════════════════════════════════════════════════
   TOKENS — warm editorial · paper + ink + electric tangerine
════════════════════════════════════════════════════════════ */
const T = {
  paper:   "#F2EDE4",  // warm bone
  paper2:  "#E9E2D5",  // raised
  ink:     "#16130E",  // near-black
  ink2:    "#3A352C",  // soft ink
  faint:   "#8C8473",  // captions
  line:    "#D6CDBB",  // hairline
  signal:  "#FF5B23",  // electric tangerine
  signalD: "#E2451A",
  forest:  "#1F3D2B",  // deep green — "shipped/live"
  cream:   "#FBF8F2",
};
const F = {
  display: "'Anton', 'Arial Narrow', sans-serif",     // heavy condensed
  body:    "'Plus Jakarta Sans', system-ui, sans-serif",
  mono:    "'Geist Mono', ui-monospace, monospace",
};

/* image base — works on GitHub Pages subpath + root */
const PUB = process.env.PUBLIC_URL || "";
const IMG = {
  // drop these files in /public  (or /public/img). Replace names as needed.
  me:  `${PUB}/Khem_Bikram_Rana.jpeg`,
  p1:  `${PUB}/img/project-pos.jpg`,
  p2:  `${PUB}/img/project-drupal.jpg`,
  p3:  `${PUB}/img/project-wordpress.jpg`,
  p4:  `${PUB}/img/project-seo.jpg`,
};

/* ════════════════════════════════════════════════════════════
   CONTENT
════════════════════════════════════════════════════════════ */
const ME = {
  name: "Khem Bikram Rana",
  location: "Kathmandu, Nepal",
  tz: "UTC+5:45",
  email: "khembikram1@gmail.com",
  web: "khembikram.com.np",
  webHref: "https://khembikram.com.np/",
  github: "github.com/khembikram",
  upworkHref: "https://www.upwork.com/freelancers/~01d5b7a497b00287c6",
  linkedin: "linkedin.com/in/khembikram",
};
const WEB3_KEY = "c0e139c5-4573-474d-bcf3-46fb1621d4ff";

const ROLES = ["FULL-STACK DEV", "SEO ENGINEER", "REACT SPECIALIST", "PROBLEM SOLVER"];

const PROJECTS = [
  {
    seq: "01", name: "RESTAURANT POS", year: "2024", cat: "Desktop · Full-Stack",
    img: IMG.p1,
    desc: "A full desktop point-of-sale built end to end. Electron + React on the front, Java Spring Boot and PostgreSQL behind it. Multi-payment, German TSE/Fiskaly fiscal compliance, kitchen routing, thermal printing.",
    tech: ["React", "Electron", "Spring Boot", "PostgreSQL"],
  },
  {
    seq: "02", name: "DRUPAL PLATFORMS", year: "2024", cat: "CMS · Full-Stack",
    img: IMG.p2,
    desc: "Enterprise Drupal 9/10 builds for agency clients — custom modules, content architecture, Views config, performance tuning, and structured-data SEO baked in.",
    tech: ["Drupal", "PHP", "MySQL", "Twig"],
  },
  {
    seq: "03", name: "WORDPRESS SITES", year: "2023", cat: "CMS · SEO",
    img: IMG.p3,
    desc: "20+ custom WordPress builds — bespoke themes, Gutenberg blocks, WooCommerce. Drove organic traffic up 60% across key accounts with real technical SEO.",
    tech: ["WordPress", "PHP", "WooCommerce", "SEO"],
  },
  {
    seq: "04", name: "SEO AUDITS", year: "2023", cat: "SEO · Strategy",
    img: IMG.p4,
    desc: "Deep technical audits across e-commerce and editorial — crawl budget, canonical mapping, schema, Core Web Vitals roadmaps. 5-star across 50+ Upwork projects.",
    tech: ["Core Web Vitals", "Schema", "GSC", "Screaming Frog"],
  },
];

const CITIES = ["NEW YORK", "LONDON", "BERLIN", "SYDNEY", "TORONTO"];

const METRICS = [
  { n: "3+", l: "YEARS SHIPPING" },
  { n: "50+", l: "PROJECTS DELIVERED" },
  { n: "60%", l: "AVG ORGANIC LIFT" },
  { n: "5.0", l: "UPWORK RATING" },
];

const STACK = [
  "React", "Next.js", "Java", "Spring Boot", "PostgreSQL", "Electron",
  "TypeScript", "WordPress", "Drupal", "PHP", "Tailwind", "Docker",
  "Core Web Vitals", "Schema", "GSC", "Linux",
];

const EXPERIENCE = [
  { company: "ANCHOR POINTS", role: "Full-Stack Developer", period: "2024 — 2025", meta: "Kathmandu",
    bullets: ["Enterprise Drupal & WordPress platforms", "Shipped a full restaurant POS system", "Led technical SEO — audits, schema, CWV", "~40% performance gains across clients"] },
  { company: "UPWORK", role: "Freelance Dev & SEO", period: "2023 — 2025", meta: "Remote · 5.0★",
    bullets: ["50+ projects at a consistent 5-star rating", "Technical SEO for 20+ sites", "WordPress, Drupal & React for US/EU clients"] },
  { company: "LQ DIGITAL", role: "Web Developer", period: "2023 — 2024", meta: "Lalitpur",
    bullets: ["Grew organic traffic 60% via SEO + perf", "20+ responsive WordPress sites", "Gutenberg block libraries & WooCommerce"] },
];

const TESTIMONIALS = [
  { role: "E-COMMERCE OWNER", text: "Fast turnaround, clean code, and the SEO gains showed up in Search Console within weeks." },
  { role: "SAAS FOUNDER", text: "Understood the brief quickly, communicated well, and the final product matched exactly what we discussed." },
  { role: "AGENCY · UK", text: "Built our custom Drupal module from scratch, handled migration, optimised performance. Would hire again." },
];

/* ════════════════════════════════════════════════════════════
   HOOKS / UTILS
════════════════════════════════════════════════════════════ */
function usePrefersReducedMotion() {
  const [r, setR] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setR(mq.matches);
    const on = (e) => setR(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return r;
}

/* graceful image with fallback block */
function Img({ src, alt, style, fallbackLabel }) {
  const [failed, setFailed] = useState(false);
  if (failed || !src) {
    return (
      <div style={{ ...style, background: `repeating-linear-gradient(135deg, ${T.paper2}, ${T.paper2} 12px, ${T.cream} 12px, ${T.cream} 24px)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontFamily: F.mono, fontSize: 11, color: T.faint, letterSpacing: "0.1em", textTransform: "uppercase", textAlign: "center", padding: 16 }}>
          {/* {fallbackLabel || "No image haha"} */}
        </span>
      </div>
    );
  }
  return <img src={src} alt={alt} onError={() => setFailed(true)} style={{ ...style, objectFit: "cover" }} />;
}

/* echoed display headline (CRAV-style duplicate/offset) */
function Echo({ children, color = T.ink, size, lh = 0.82, className = "" }) {
  return (
    <h2 className={className} style={{ position: "relative", margin: 0, fontFamily: F.display, fontWeight: 400, lineHeight: lh, letterSpacing: "-0.02em", fontSize: size, textTransform: "uppercase" }}>
      <span style={{ display: "block", color }}>{children}</span>
      <span aria-hidden style={{ display: "block", color: "transparent", WebkitTextStroke: `1.4px ${T.line}`, marginTop: "-0.06em" }}>{children}</span>
    </h2>
  );
}

function Marquee({ items, reverse }) {
  const seq = [...items, ...items, ...items];
  return (
    <div style={{ overflow: "hidden", borderTop: `1px solid ${T.ink}`, borderBottom: `1px solid ${T.ink}`, background: T.ink }}>
      <div className={reverse ? "kbr-mq-r" : "kbr-mq"} style={{ display: "inline-flex", whiteSpace: "nowrap", padding: "14px 0" }}>
        {seq.map((it, i) => (
          <span key={i} style={{ fontFamily: F.display, fontSize: "clamp(20px,3vw,34px)", color: T.paper, letterSpacing: "-0.01em", padding: "0 28px", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 28 }}>
            {it}<span style={{ color: T.signal, fontSize: "0.7em" }}>✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   PRELOADER  (CRAV "PREPARING ARTISAN KITCHEN…")
════════════════════════════════════════════════════════════ */
function Preloader({ onDone, reduced }) {
  const root = useRef(null);
  const [pct, setPct] = useState(0);
  const done = pct >= 100;

  useEffect(() => {
    if (reduced) { onDone(); return; }
    let v = 0;
    const id = setInterval(() => {
      v += Math.random() * 16 + 6;
      if (v >= 100) { v = 100; clearInterval(id); }
      setPct(Math.floor(v));
    }, 130);
    return () => clearInterval(id);
  }, [reduced, onDone]);

  useGSAP(() => {
    if (reduced) return;
    if (done) {
      gsap.to(root.current, { yPercent: -100, duration: 0.7, ease: "power4.inOut", delay: 0.55, onComplete: onDone });
    }
  }, { dependencies: [done], scope: root });

  return (
    <div ref={root} style={{ position: "fixed", inset: 0, zIndex: 9999, background: T.ink, color: T.paper, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 22 }}>
      <div style={{ fontFamily: F.mono, fontSize: 12, letterSpacing: "0.3em", color: T.signal }}>
        {done ? "✓ BUILD COMPLETE" : "COMPILING PORTFOLIO…"}
      </div>
      <div style={{ fontFamily: F.display, fontSize: "clamp(56px,16vw,160px)", lineHeight: 1, letterSpacing: "-0.03em" }}>{pct}<span style={{ color: T.signal }}>%</span></div>
      <div style={{ width: "min(320px,70vw)", height: 2, background: "rgba(242,237,228,0.15)", overflow: "hidden" }}>
        <div style={{ height: "100%", background: T.signal, transform: `scaleX(${pct / 100})`, transformOrigin: "left", transition: "transform .13s linear" }} />
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   NAV
════════════════════════════════════════════════════════════ */
function Nav({ onNav, active }) {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  useEffect(() => {
    const on = () => setSolid(window.scrollY > 40);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const links = [["work", "Work"], ["stack", "Stack"], ["about", "About"], ["contact", "Contact"]];
  const go = (id) => { onNav(id); setOpen(false); };
  return (
    <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 200, background: solid ? "rgba(242,237,228,0.86)" : "transparent", backdropFilter: solid ? "blur(12px)" : "none", borderBottom: `1px solid ${solid ? T.line : "transparent"}`, transition: "all .25s" }}>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "0 22px", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => go("top")} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: F.display, fontSize: 26, color: T.ink, letterSpacing: "-0.02em" }}>
          KBR<span style={{ color: T.signal }}>.</span>
        </button>
        <div className="kbr-nav-desk" style={{ display: "flex", gap: 4, alignItems: "center" }}>
          {links.map(([id, label]) => (
            <button key={id} onClick={() => go(id)} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: F.mono, fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase", padding: "8px 14px", color: active === id ? T.ink : T.faint }}>
              {label}
            </button>
          ))}
          <button onClick={() => go("contact")} style={{ marginLeft: 10, fontFamily: F.mono, fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase", padding: "9px 18px", background: T.signal, color: T.cream, border: "none", borderRadius: 2, cursor: "pointer" }}>
            Hire me
          </button>
        </div>
        <button className="kbr-burger" onClick={() => setOpen((o) => !o)} style={{ display: "none", background: "none", border: `1px solid ${T.ink}`, color: T.ink, padding: "6px 11px", cursor: "pointer", fontFamily: F.mono, fontSize: 16 }}>
          {open ? "✕" : "≡"}
        </button>
      </div>
      {open && (
        <div style={{ borderTop: `1px solid ${T.line}`, background: T.cream, padding: "10px 22px 18px" }}>
          {links.map(([id, label]) => (
            <button key={id} onClick={() => go(id)} style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", borderBottom: `1px solid ${T.line}`, padding: "14px 0", fontFamily: F.display, fontSize: 22, color: T.ink, cursor: "pointer", textTransform: "uppercase" }}>
              {label}
            </button>
          ))}
          <button onClick={() => go("contact")} style={{ marginTop: 14, width: "100%", padding: "13px 0", background: T.signal, color: T.cream, border: "none", fontFamily: F.mono, fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer" }}>Hire me</button>
        </div>
      )}
    </nav>
  );
}

/* ════════════════════════════════════════════════════════════
   HERO  — echoed display + role swap + photo reveal
════════════════════════════════════════════════════════════ */
function Hero({ onNav, reduced, ready }) {
  const root = useRef(null);
  const [role, setRole] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setRole((r) => (r + 1) % ROLES.length), 2200);
    return () => clearInterval(id);
  }, []);

  useGSAP(() => {
    if (reduced || !ready) return;
    const tl = gsap.timeline({ defaults: { ease: "power4.out" }, delay: 0.1 });
    tl.from(".kbr-h-eyebrow", { y: 20, opacity: 0, duration: 0.5 })
      .from(".kbr-h-line", { yPercent: 110, duration: 0.85, stagger: 0.08 }, "-=0.2")
      .from(".kbr-h-photo", { scale: 1.15, opacity: 0, duration: 0.9, ease: "power3.out" }, "-=0.6")
      .from(".kbr-h-foot > *", { y: 18, opacity: 0, stagger: 0.1, duration: 0.5 }, "-=0.5");

    // parallax on photo
    gsap.to(".kbr-h-photo img", {
      yPercent: 12, ease: "none",
      scrollTrigger: { trigger: root.current, start: "top top", end: "bottom top", scrub: true },
    });
  }, { scope: root, dependencies: [reduced, ready] });

  return (
    <header ref={root} id="top" style={{ position: "relative", maxWidth: 1240, margin: "0 auto", padding: "clamp(96px,14vw,150px) 22px clamp(40px,6vw,72px)" }}>
      <div className="kbr-h-eyebrow" style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: "clamp(20px,3vw,34px)", flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: F.mono, fontSize: 12, color: T.forest, fontWeight: 600 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.forest, animation: "kbrPulse 2.4s infinite" }} /> OPEN TO WORK
        </span>
        <span style={{ fontFamily: F.mono, fontSize: 12, color: T.faint, letterSpacing: "0.06em" }}>EST. KATHMANDU · {ME.tz}</span>
      </div>

      <div className="kbr-hero-grid" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "clamp(20px,4vw,48px)", alignItems: "end" }}>
        <div>
          <h1 style={{ margin: 0, fontFamily: F.display, fontWeight: 400, lineHeight: 0.84, letterSpacing: "-0.03em", textTransform: "uppercase", fontSize: "clamp(58px,12.5vw,170px)", color: T.ink }}>
            <span style={{ display: "block", overflow: "hidden" }}><span className="kbr-h-line" style={{ display: "block" }}>I SHIP</span></span>
            <span style={{ display: "block", overflow: "hidden" }}><span className="kbr-h-line" style={{ display: "block", color: T.signal }}>THINGS</span></span>
            <span style={{ display: "block", overflow: "hidden" }}><span className="kbr-h-line" style={{ display: "block" }}>THAT WORK</span></span>
          </h1>
        </div>

        <div className="kbr-h-photo" style={{ width: "clamp(160px,22vw,280px)", aspectRatio: "3/4", overflow: "hidden", borderRadius: 4, border: `1px solid ${T.line}`, flexShrink: 0, alignSelf: "stretch", maxHeight: 360 }}>
          <Img src={IMG.me} alt={ME.name} fallbackLabel={`add /public/${"Khem_Bikram_Rana.jpeg"}`} style={{ width: "100%", height: "100%", objectPosition: "center top" }} />
        </div>
      </div>

      <div className="kbr-h-foot" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 24, marginTop: "clamp(28px,4vw,48px)", borderTop: `1px solid ${T.line}`, paddingTop: 24 }}>
        <p style={{ margin: 0, fontFamily: F.body, fontSize: "clamp(14px,1.6vw,17px)", color: T.ink2, lineHeight: 1.6, maxWidth: 440 }}>
          Full-stack developer & SEO engineer from {ME.location}. Real systems for real clients — POS platforms, CMS builds, and SEO that actually moves Search Console.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, alignItems: "flex-start" }}>
          <span style={{ fontFamily: F.mono, fontSize: 13, color: T.signal, letterSpacing: "0.04em", minHeight: 18 }}>› {ROLES[role]}</span>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={() => onNav("work")} style={{ fontFamily: F.mono, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.04em", padding: "12px 22px", background: T.ink, color: T.paper, border: "none", borderRadius: 2, cursor: "pointer" }}>See the work →</button>
            <button onClick={() => onNav("contact")} style={{ fontFamily: F.mono, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.04em", padding: "12px 22px", background: "transparent", color: T.ink, border: `1px solid ${T.ink}`, borderRadius: 2, cursor: "pointer" }}>Start a project</button>
          </div>
        </div>
      </div>
    </header>
  );
}

/* ════════════════════════════════════════════════════════════
   METRICS (count-up)
════════════════════════════════════════════════════════════ */
function Metrics({ reduced }) {
  const root = useRef(null);
  useGSAP(() => {
    if (reduced) return;
    gsap.utils.toArray(".kbr-m").forEach((el) => {
      const ne = el.querySelector(".kbr-mn");
      const raw = ne.dataset.v; const num = parseFloat(raw); const suf = raw.replace(/[\d.]/g, "");
      const o = { v: 0 };
      ScrollTrigger.create({ trigger: el, start: "top 88%", once: true, onEnter: () => {
        gsap.to(o, { v: num, duration: 1.1, ease: "power2.out", onUpdate: () => { ne.textContent = (Number.isInteger(num) ? Math.round(o.v) : o.v.toFixed(1)) + suf; } });
      }});
    });
  }, { scope: root, dependencies: [reduced] });
  return (
    <section ref={root} style={{ background: T.ink, color: T.paper }}>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "clamp(40px,6vw,64px) 22px", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 24 }} className="kbr-m-grid">
        {METRICS.map((m, i) => (
          <div key={i} className="kbr-m" style={{ textAlign: "center" }}>
            <div className="kbr-mn" data-v={m.n} style={{ fontFamily: F.display, fontSize: "clamp(40px,7vw,84px)", lineHeight: 1, color: T.signal, letterSpacing: "-0.02em" }}>{m.n}</div>
            <div style={{ fontFamily: F.mono, fontSize: 11, color: "rgba(242,237,228,0.6)", letterSpacing: "0.1em", marginTop: 8 }}>{m.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════
   WORK — pinned showcase (CRAV burger-layer pin)
   Left panel pins; right list scrolls; active project swaps.
════════════════════════════════════════════════════════════ */
function Work({ reduced }) {
  const root = useRef(null);
  const [active, setActive] = useState(0);

  useGSAP(() => {
    if (reduced) return;
    if (window.innerWidth < 900) return; // mobile = simple stacked
    const panel = root.current.querySelector(".kbr-pin-panel");
    ScrollTrigger.create({
      trigger: ".kbr-pin-wrap", start: "top 80px", end: "bottom bottom",
      pin: panel, pinSpacing: false,
    });
    gsap.utils.toArray(".kbr-pin-item").forEach((el, i) => {
      ScrollTrigger.create({
        trigger: el, start: "top center", end: "bottom center",
        onToggle: (self) => { if (self.isActive) setActive(i); },
      });
    });
  }, { scope: root, dependencies: [reduced] });

  const p = PROJECTS[active];

  return (
    <section ref={root} id="work" style={{ maxWidth: 1240, margin: "0 auto", padding: "clamp(72px,11vw,128px) 22px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontFamily: F.mono, fontSize: 12, color: T.signal, letterSpacing: "0.2em" }}>// SELECTED WORK</span>
      </div>
      <Echo size="clamp(44px,9vw,120px)" color={T.ink}>SHIPPED &amp; LIVE</Echo>

      <div className="kbr-pin-wrap" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(24px,4vw,56px)", marginTop: "clamp(36px,5vw,64px)", alignItems: "start" }}>
        {/* pinned visual panel */}
        <div className="kbr-pin-panel" style={{ position: "relative" }}>
          <div style={{ aspectRatio: "4/5", borderRadius: 6, overflow: "hidden", border: `1px solid ${T.line}`, position: "relative", background: T.paper2 }}>
            {PROJECTS.map((proj, i) => (
              <Img key={i} src={proj.img} alt={proj.name} fallbackLabel={`add ${proj.name} screenshot`}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: i === active ? 1 : 0, transition: "opacity .5s ease" }} />
            ))}
            <div style={{ position: "absolute", left: 16, top: 16, fontFamily: F.display, fontSize: 64, lineHeight: 1, color: T.cream, mixBlendMode: "difference" }}>{p.seq}</div>
            <div style={{ position: "absolute", left: 16, bottom: 16, display: "inline-flex", alignItems: "center", gap: 7, background: T.forest, color: T.paper, fontFamily: F.mono, fontSize: 11, padding: "5px 11px", borderRadius: 2, letterSpacing: "0.08em" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.signal }} /> LIVE
            </div>
          </div>
        </div>

        {/* scrolling list */}
        <div>
          {PROJECTS.map((proj, i) => (
            <div key={proj.seq} className="kbr-pin-item" style={{ minHeight: "62vh", display: "flex", flexDirection: "column", justifyContent: "center", padding: "20px 0", borderBottom: `1px solid ${T.line}`, opacity: i === active ? 1 : 0.4, transition: "opacity .3s" }}>
              <span style={{ fontFamily: F.mono, fontSize: 11, color: T.faint, letterSpacing: "0.1em", marginBottom: 10 }}>{proj.year} · {proj.cat}</span>
              <h3 style={{ margin: "0 0 16px", fontFamily: F.display, fontSize: "clamp(30px,5vw,58px)", lineHeight: 0.92, color: T.ink, letterSpacing: "-0.02em" }}>{proj.name}</h3>
              <p style={{ margin: "0 0 18px", fontFamily: F.body, fontSize: 15, color: T.ink2, lineHeight: 1.65, maxWidth: 440 }}>{proj.desc}</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {proj.tech.map((t) => (
                  <span key={t} style={{ fontFamily: F.mono, fontSize: 12, color: T.ink2, border: `1px solid ${T.line}`, borderRadius: 3, padding: "5px 11px" }}>{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════
   SHIPS WORLDWIDE (CRAV "quality that travels")
════════════════════════════════════════════════════════════ */
function Worldwide({ reduced }) {
  const root = useRef(null);
  useGSAP(() => {
    if (reduced) return;
    gsap.from(".kbr-city", {
      scrollTrigger: { trigger: root.current, start: "top 78%" },
      y: 30, opacity: 0, stagger: 0.1, duration: 0.6, ease: "power3.out",
    });
  }, { scope: root, dependencies: [reduced] });
  return (
    <section ref={root} style={{ background: T.forest, color: T.paper }}>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "clamp(64px,9vw,110px) 22px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 20, marginBottom: "clamp(32px,5vw,56px)" }}>
          <div>
            <span style={{ fontFamily: F.mono, fontSize: 12, color: T.signal, letterSpacing: "0.2em" }}>// REACH</span>
            <h2 style={{ margin: "12px 0 0", fontFamily: F.display, fontSize: "clamp(38px,7vw,96px)", lineHeight: 0.9, letterSpacing: "-0.02em" }}>WORK THAT<br />TRAVELS.</h2>
          </div>
          <p style={{ margin: 0, fontFamily: F.body, fontSize: 15, color: "rgba(242,237,228,0.75)", lineHeight: 1.65, maxWidth: 320 }}>
            Built remotely from Kathmandu, shipped for clients across the US, UK, and Europe. Time zones are a feature, not a bug — your morning is my build window.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", borderTop: `1px solid rgba(242,237,228,0.18)` }}>
          {CITIES.map((c) => (
            <div key={c} className="kbr-city" style={{ padding: "clamp(20px,3vw,32px) 8px", borderBottom: `1px solid rgba(242,237,228,0.18)`, borderRight: `1px solid rgba(242,237,228,0.18)`, fontFamily: F.display, fontSize: "clamp(20px,2.6vw,32px)", letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ color: T.signal, fontSize: "0.6em" }}>✦</span>{c}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════
   ABOUT + EXPERIENCE + TESTIMONIALS
════════════════════════════════════════════════════════════ */
function About({ reduced }) {
  const root = useRef(null);
  useGSAP(() => {
    if (reduced) return;
    gsap.from(".kbr-exp", { scrollTrigger: { trigger: ".kbr-exp-list", start: "top 78%" }, x: -24, opacity: 0, stagger: 0.14, duration: 0.6, ease: "power3.out" });
    gsap.from(".kbr-q", { scrollTrigger: { trigger: ".kbr-q-grid", start: "top 82%" }, y: 24, opacity: 0, stagger: 0.1, duration: 0.55, ease: "power3.out" });
  }, { scope: root, dependencies: [reduced] });

  return (
    <section ref={root} id="about" style={{ maxWidth: 1240, margin: "0 auto", padding: "clamp(72px,11vw,128px) 22px" }}>
      <span style={{ fontFamily: F.mono, fontSize: 12, color: T.signal, letterSpacing: "0.2em" }}>// WHO'S BEHIND THE COMMITS</span>
      <div className="kbr-about-grid" style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: "clamp(32px,5vw,72px)", marginTop: "clamp(24px,3vw,40px)", alignItems: "start" }}>
        <div>
          <Echo size="clamp(34px,5.5vw,72px)" color={T.ink}>I GIVE MY BEST.</Echo>
          <p style={{ margin: "24px 0 16px", fontFamily: F.body, fontSize: 16, color: T.ink2, lineHeight: 1.75 }}>
            I'm Khem — a full-stack developer and SEO engineer from Kathmandu. I've worked on real systems people use daily: custom POS platforms, agency Drupal and WordPress builds, and SEO campaigns that actually moved the needle.
          </p>
          <p style={{ margin: 0, fontFamily: F.body, fontSize: 16, color: T.ink2, lineHeight: 1.75 }}>
            Broken build at 2am or an SEO audit that needs to make sense — I'm in. Clean code, fast sites, work that holds up.
          </p>
          <div style={{ marginTop: 28, display: "flex", flexWrap: "wrap", gap: 8 }}>
            {STACK.map((s) => (
              <span key={s} style={{ fontFamily: F.mono, fontSize: 12, color: T.ink2, border: `1px solid ${T.line}`, borderRadius: 3, padding: "6px 12px", background: T.cream }}>{s}</span>
            ))}
          </div>
        </div>

        <div className="kbr-exp-list">
          {EXPERIENCE.map((e, i) => (
            <div key={i} className="kbr-exp" style={{ padding: "22px 0", borderBottom: `1px solid ${T.line}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontFamily: F.display, fontSize: "clamp(22px,3vw,34px)", color: T.ink, letterSpacing: "-0.01em" }}>{e.company}</span>
                <span style={{ fontFamily: F.mono, fontSize: 11, color: T.faint }}>{e.period} · {e.meta}</span>
              </div>
              <div style={{ fontFamily: F.mono, fontSize: 12, color: T.signal, margin: "6px 0 12px" }}>{e.role}</div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
                {e.bullets.map((b, j) => (
                  <li key={j} style={{ display: "flex", gap: 9, fontFamily: F.body, fontSize: 14, color: T.ink2, lineHeight: 1.55 }}>
                    <span style={{ color: T.signal, fontFamily: F.mono }}>—</span>{b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: "clamp(56px,8vw,90px)" }}>
        <span style={{ fontFamily: F.mono, fontSize: 12, color: T.signal, letterSpacing: "0.2em" }}>// STRAIGHT FROM CLIENTS</span>
        <div className="kbr-q-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 16, marginTop: 28 }}>
          {TESTIMONIALS.map((t, i) => (
            <figure key={i} className="kbr-q" style={{ margin: 0, padding: "26px 24px", background: T.cream, border: `1px solid ${T.line}`, borderRadius: 6 }}>
              <div style={{ fontFamily: F.mono, fontSize: 14, color: T.signal, marginBottom: 14 }}>★★★★★</div>
              <blockquote style={{ margin: "0 0 16px", fontFamily: F.body, fontSize: 15, color: T.ink, lineHeight: 1.65 }}>“{t.text}”</blockquote>
              <figcaption style={{ fontFamily: F.mono, fontSize: 11, color: T.faint, letterSpacing: "0.08em" }}>{t.role}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════
   CONTACT
════════════════════════════════════════════════════════════ */
function Contact({ reduced }) {
  const root = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", subject: "", msg: "" });
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");

  const genQ = useCallback(() => {
    const ops = ["+", "−", "×"]; const op = ops[Math.floor(Math.random() * 3)];
    let a = Math.floor(Math.random() * 9) + 1, b = Math.floor(Math.random() * 9) + 1;
    if (op === "−" && b > a) [a, b] = [b, a];
    const ans = op === "+" ? a + b : op === "−" ? a - b : a * b;
    return { q: `${a} ${op} ${b}`, ans };
  }, []);
  const [cap, setCap] = useState(genQ);
  const [capVal, setCapVal] = useState(""); const [capOk, setCapOk] = useState(false); const [capErr, setCapErr] = useState(false);
  const onCap = (v) => { setCapVal(v); const n = parseInt(v, 10); const ok = !isNaN(n) && n === cap.ans; setCapOk(ok); if (ok) setCapErr(false); };
  const newCap = () => { setCap(genQ()); setCapVal(""); setCapOk(false); setCapErr(false); };

  useGSAP(() => {
    if (reduced) return;
    gsap.from(".kbr-c", { scrollTrigger: { trigger: root.current, start: "top 80%" }, y: 24, opacity: 0, stagger: 0.08, duration: 0.55, ease: "power3.out" });
  }, { scope: root, dependencies: [reduced] });

  const links = [
    ["EMAIL", ME.email, `mailto:${ME.email}`],
    ["UPWORK", "upwork profile", ME.upworkHref],
    ["GITHUB", ME.github, `https://${ME.github}`],
    ["LINKEDIN", ME.linkedin, `https://${ME.linkedin}`],
  ];

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.msg.trim()) { setErr("Fill in your name, email, and message."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setErr("Enter a valid email address."); return; }
    if (!capOk) { setCapErr(true); setErr("Solve the math check first."); return; }
    setErr(""); setStatus("sending");
    try {
      const res = await fetch("https://api.web3forms.com/submit", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ access_key: WEB3_KEY, name: form.name, email: form.email, subject: form.subject || `Portfolio contact from ${form.name}`, message: form.msg, from_name: "Portfolio Contact Form", botcheck: "" }) });
      const data = await res.json();
      if (data.success) { setStatus("success"); setForm({ name: "", email: "", subject: "", msg: "" }); newCap(); }
      else { setStatus("error"); setErr(data.message || "Something went wrong."); }
    } catch { setStatus("error"); setErr("Network error — try again."); }
  };

  const field = { width: "100%", padding: "12px 0", background: "transparent", border: "none", borderBottom: `1px solid ${T.line}`, color: T.ink, fontSize: 15, outline: "none", fontFamily: F.body, boxSizing: "border-box" };
  const lbl = { fontFamily: F.mono, fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: T.faint, display: "block", marginBottom: 8 };

  return (
    <section ref={root} id="contact" style={{ background: T.ink, color: T.paper }}>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "clamp(72px,11vw,128px) 22px" }}>
        <span className="kbr-c" style={{ fontFamily: F.mono, fontSize: 12, color: T.signal, letterSpacing: "0.2em" }}>// LET'S BUILD</span>
        <h2 className="kbr-c" style={{ margin: "14px 0 0", fontFamily: F.display, fontSize: "clamp(46px,10vw,140px)", lineHeight: 0.84, letterSpacing: "-0.03em" }}>
          GOT SOMETHING<br /><span style={{ color: T.signal }}>TO BUILD?</span>
        </h2>

        <div className="kbr-contact-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(32px,5vw,72px)", marginTop: "clamp(40px,6vw,72px)", alignItems: "start" }}>
          <div className="kbr-c">
            <p style={{ fontFamily: F.body, fontSize: 15, color: "rgba(242,237,228,0.72)", lineHeight: 1.75, margin: "0 0 28px" }}>
              Freelance, full-time, or a fire to put out — tell me what you're building. I reply fast and I give my best.
            </p>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 9, fontFamily: F.mono, fontSize: 12, color: T.signal, border: `1px solid rgba(242,237,228,0.2)`, borderRadius: 4, padding: "9px 14px", marginBottom: 28 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.signal, animation: "kbrPulse 2.4s infinite" }} /> {ME.location} · usually replies within ~2h
            </div>
            {links.map(([l, v, h]) => (
              <a key={l} href={h} style={{ display: "flex", justifyContent: "space-between", padding: "14px 0", borderBottom: `1px solid rgba(242,237,228,0.15)`, textDecoration: "none" }}
                onMouseEnter={(e) => (e.currentTarget.querySelector("span:last-child").style.color = T.signal)}
                onMouseLeave={(e) => (e.currentTarget.querySelector("span:last-child").style.color = T.paper)}>
                <span style={{ fontFamily: F.mono, fontSize: 11, color: "rgba(242,237,228,0.5)", letterSpacing: "0.12em" }}>{l}</span>
                <span style={{ fontFamily: F.mono, fontSize: 13, color: T.paper, transition: "color .15s" }}>{v} ↗</span>
              </a>
            ))}
          </div>

          <div className="kbr-c">
            {status === "success" ? (
              <div style={{ padding: "40px 0", textAlign: "center" }}>
                <div style={{ width: 52, height: 52, margin: "0 auto 18px", borderRadius: "50%", border: `2px solid ${T.signal}`, color: T.signal, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: F.mono, fontSize: 22 }}>✓</div>
                <div style={{ fontFamily: F.display, fontSize: 30, color: T.paper }}>MESSAGE RECEIVED</div>
                <p style={{ fontFamily: F.body, fontSize: 14, color: "rgba(242,237,228,0.7)", marginTop: 8 }}>I'll reply at {ME.email} soon.</p>
                <button onClick={() => setStatus("idle")} style={{ marginTop: 20, padding: "10px 20px", background: "transparent", border: `1px solid rgba(242,237,228,0.3)`, color: T.paper, fontFamily: F.mono, fontSize: 12, cursor: "pointer", borderRadius: 2 }}>send another →</button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {[["name", "name", "Your full name", "text", true], ["email", "email", "you@example.com", "email", true], ["subject", "subject", "What's this about?", "text", false]].map(([k, l, ph, type, req]) => (
                  <div key={k}>
                    <label style={{ ...lbl, color: "rgba(242,237,228,0.5)" }}>{l}{req && <span style={{ color: T.signal }}> *</span>}</label>
                    <input type={type} value={form[k]} placeholder={ph} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                      onFocus={(e) => (e.target.style.borderColor = T.signal)} onBlur={(e) => (e.target.style.borderColor = T.line)}
                      style={{ ...field, color: T.paper, borderBottomColor: "rgba(242,237,228,0.25)" }} />
                  </div>
                ))}
                <div>
                  <label style={{ ...lbl, color: "rgba(242,237,228,0.5)" }}>message<span style={{ color: T.signal }}> *</span></label>
                  <textarea rows={4} value={form.msg} placeholder="Tell me about your project…" onChange={(e) => setForm((p) => ({ ...p, msg: e.target.value }))}
                    onFocus={(e) => (e.target.style.borderColor = T.signal)} onBlur={(e) => (e.target.style.borderColor = T.line)}
                    style={{ ...field, color: T.paper, borderBottomColor: "rgba(242,237,228,0.25)", resize: "vertical", paddingTop: 10 }} />
                </div>
                <div>
                  <label style={{ ...lbl, color: "rgba(242,237,228,0.5)" }}>human check<span style={{ color: T.signal }}> *</span></label>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", border: `1px solid ${capErr ? T.signal : capOk ? T.forest : "rgba(242,237,228,0.25)"}`, borderRadius: 4 }}>
                    <span style={{ fontFamily: F.mono, fontSize: 15, color: T.paper }}>{cap.q} = ?</span>
                    <input type="number" value={capVal} onChange={(e) => onCap(e.target.value)} placeholder="answer" style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", borderBottom: `1px solid rgba(242,237,228,0.25)`, color: T.paper, fontSize: 14, outline: "none", fontFamily: F.body, padding: "2px 0" }} />
                    <button type="button" onClick={newCap} style={{ background: "none", border: "none", color: "rgba(242,237,228,0.6)", cursor: "pointer", fontSize: 15 }}>↺</button>
                  </div>
                  {capOk && <p style={{ fontFamily: F.mono, fontSize: 11, color: T.signal, marginTop: 6 }}>✓ verified</p>}
                </div>
                {err && <p style={{ fontFamily: F.mono, fontSize: 12, color: T.signal, margin: 0 }}>{err}</p>}
                <button onClick={submit} disabled={status === "sending"} style={{ padding: "15px 0", background: T.signal, color: T.cream, border: "none", borderRadius: 2, fontFamily: F.mono, fontSize: 13, letterSpacing: "0.06em", textTransform: "uppercase", cursor: status === "sending" ? "not-allowed" : "pointer", opacity: status === "sending" ? 0.7 : 1 }}>
                  {status === "sending" ? "sending…" : "send message →"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════
   FOOTER (big CRAV-style wordmark)
════════════════════════════════════════════════════════════ */
function Footer({ onNav }) {
  return (
    <footer style={{ background: T.ink, color: T.paper, borderTop: `1px solid rgba(242,237,228,0.12)` }}>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "clamp(40px,6vw,64px) 22px 28px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 24, marginBottom: 32 }}>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            {[["work", "Work"], ["stack", "Stack"], ["about", "About"], ["contact", "Contact"]].map(([id, l]) => (
              <button key={id} onClick={() => onNav(id)} style={{ background: "none", border: "none", color: "rgba(242,237,228,0.7)", cursor: "pointer", fontFamily: F.mono, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.06em" }}>{l}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {[["Upwork", ME.upworkHref], ["GitHub", `https://${ME.github}`], ["LinkedIn", `https://${ME.linkedin}`], ["Email", `mailto:${ME.email}`]].map(([l, h]) => (
              <a key={l} href={h} style={{ color: "rgba(242,237,228,0.7)", textDecoration: "none", fontFamily: F.mono, fontSize: 13 }}>{l} ↗</a>
            ))}
          </div>
        </div>
        <div style={{ fontFamily: F.display, fontSize: "clamp(60px,18vw,260px)", lineHeight: 0.8, letterSpacing: "-0.03em", color: T.paper, overflow: "hidden" }}>
          KHEM<span style={{ color: T.signal }}>.</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginTop: 20, borderTop: `1px solid rgba(242,237,228,0.12)`, paddingTop: 18 }}>
          <span style={{ fontFamily: F.mono, fontSize: 11, color: "rgba(242,237,228,0.5)" }}>© {new Date().getFullYear()} {ME.name} — All rights reserved</span>
          <span style={{ fontFamily: F.mono, fontSize: 11, color: "rgba(242,237,228,0.5)" }}>clean code · fast sites · est. Kathmandu</span>
        </div>
      </div>
    </footer>
  );
}

/* ════════════════════════════════════════════════════════════
   ROOT
════════════════════════════════════════════════════════════ */
export default function App() {
  const reduced = usePrefersReducedMotion();
  const [ready, setReady] = useState(false);
  const [active, setActive] = useState("top");

  const onNav = useCallback((id) => {
    if (id === "top") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    if (!ready) return;
    const ids = ["work", "about", "contact"];
    const obs = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }), { rootMargin: "-45% 0px -50% 0px" });
    ids.forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [ready]);

  // refresh ScrollTrigger after preloader so pin math is correct
  useEffect(() => {
    if (ready) { setTimeout(() => ScrollTrigger.refresh(), 100); }
  }, [ready]);

  return (
    <div style={{ background: T.paper, minHeight: "100vh", color: T.ink, overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Geist+Mono:wght@400;500&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        html{scroll-behavior:smooth;}
        body{background:${T.paper};}
        ::selection{background:${T.signal};color:${T.cream};}
        @keyframes kbrPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(1.9)}}
        @keyframes kbrMarquee{from{transform:translateX(0)}to{transform:translateX(-33.333%)}}
        @keyframes kbrMarqueeR{from{transform:translateX(-33.333%)}to{transform:translateX(0)}}
        .kbr-mq{animation:kbrMarquee 26s linear infinite;}
        .kbr-mq-r{animation:kbrMarqueeR 26s linear infinite;}
        input::placeholder,textarea::placeholder{color:${T.faint};}
        input,textarea{-webkit-appearance:none;appearance:none;}
        input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;}
        a,button{-webkit-tap-highlight-color:transparent;}
        *:focus-visible{outline:2px solid ${T.signal};outline-offset:2px;}
        @media(max-width:900px){
          .kbr-hero-grid{grid-template-columns:1fr !important;}
          .kbr-h-photo{order:-1;width:clamp(180px,55vw,260px) !important;margin-bottom:16px;}
          .kbr-pin-wrap{grid-template-columns:1fr !important;}
          .kbr-pin-panel{position:relative !important;}
          .kbr-pin-item{min-height:auto !important;opacity:1 !important;}
          .kbr-about-grid,.kbr-contact-grid{grid-template-columns:1fr !important;}
          .kbr-m-grid{grid-template-columns:repeat(2,1fr) !important;row-gap:32px;}
        }
        @media(max-width:680px){
          .kbr-nav-desk{display:none !important;}
          .kbr-burger{display:inline-block !important;}
        }
        @media(prefers-reduced-motion:reduce){
          .kbr-mq,.kbr-mq-r{animation:none !important;}
          *{animation:none !important;}
        }
      `}</style>

      {!ready && <Preloader reduced={reduced} onDone={() => setReady(true)} />}

      <Nav onNav={onNav} active={active} />
      <Hero onNav={onNav} reduced={reduced} ready={ready} />
      <Marquee items={["REACT", "SPRING BOOT", "POSTGRESQL", "WORDPRESS", "DRUPAL", "SEO", "NEXT.JS"]} />
      <Metrics reduced={reduced} />
      <Work reduced={reduced} />
      <Worldwide reduced={reduced} />
      <Marquee items={["CLEAN CODE", "FAST SITES", "REAL CLIENTS", "SHIPPED & LIVE"]} reverse />
      <About reduced={reduced} />
      <Contact reduced={reduced} />
      <Footer onNav={onNav} />
    </div>
  );
}